/*
* Copyright (c) 2009-2022. Authors: see NOTICE file.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*      http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

// Web Push (zaya-lis-chat/docs/enhancements/notification-triggers.spec.md R6-R9) — M2 rewrite.
// Registers `public/sw.js`, requests Notification permission, subscribes via `PushManager` using
// the server's VAPID public key, and hands the subscription to chat-service. Best-effort by design.
//
// KEPT HOST-SIDE per the integration-reviewer's INT-M1 note (M1's LIS push placement was accepted
// with a caveat: do it host-side for M2). The registration/subscribe step needs `chat-service`'s
// `/push/vapid` + `/push/subscriptions`, which the SDK's `ChatClient` normally calls — but that
// client now lives INSIDE the island (FR-05), unreachable from host code. Rather than reaching into
// the island's internals (or moving push into the island the way LIS's M1 did), this makes its OWN
// two plain `fetch()` calls using EXACTLY the two values the host already owns because it builds
// `ChatHost` from them anyway: `host.chatBaseUrl` + `host.getAccessToken()` (mirrors the SDK's
// `HttpClient.request()` — same headers, same base-URL join, same error tolerance — just inlined
// here so this file needs no SDK/ChatClient dependency at all).
const SW_URL = '/sw.js';
const ACTIVE_CONVERSATION_MESSAGE_TYPE = 'chat:active-conversation';
const NAVIGATE_MESSAGE_TYPE = 'chat:push-navigate';
const NAVIGATE_QUERY_PARAM = 'chat_conversation';

let registerStarted = false;

function supportsPush() {
  return typeof window !== 'undefined' && typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator && 'PushManager' in window;
}

// VAPID public keys are base64url-encoded; PushManager wants a raw Uint8Array.
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from(Array.from(rawData, char => char.charCodeAt(0)));
}

// A minimal stand-in for the SDK's `HttpClient.request()` (auth header, JSON body/response, 2xx
// check) — see zaya-lis-chat/sdk/src/rest/httpClient.ts, whose behaviour this mirrors exactly.
async function chatFetch(host, method, path, body) {
  const token = await host.getAccessToken();
  const headers = { Authorization: `Bearer ${token}` };
  let requestBody;
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    requestBody = JSON.stringify(body);
  }
  const baseUrl = host.chatBaseUrl.replace(/\/$/, '');
  const res = await fetch(`${baseUrl}${path}`, { method, headers, body: requestBody });
  if (!res.ok) {
    throw new Error(`chat-service ${method} ${path} -> ${res.status}`);
  }
  if (res.status === 204) {
    return undefined;
  }
  const text = await res.text();
  return text.length > 0 ? JSON.parse(text) : undefined;
}

// No-ops (not an error) if the browser doesn't support push, permission is denied, or a
// subscription already exists — safe to call on every chat mount. `host` is the same ChatHost
// object passed to `mount(el, host)` (only `chatBaseUrl`/`getAccessToken` are used here).
export async function registerPushNotifications(host) {
  if (registerStarted || !supportsPush()) {
    return;
  }
  registerStarted = true;
  try {
    const registration = await navigator.serviceWorker.register(SW_URL);

    let permission = Notification.permission;
    if (permission === 'default') {
      permission = await Notification.requestPermission();
    }
    if (permission !== 'granted') {
      return;
    }

    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      const { public_key: publicKey } = await chatFetch(host, 'GET', '/push/vapid');
      // An empty key means Web Push is disabled in this deployment (no VAPID keys configured
      // server-side) — skip the subscribe flow rather than erroring.
      if (!publicKey) {
        return;
      }
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }

    const { keys } = subscription.toJSON();
    if (!keys || !keys.p256dh || !keys.auth) {
      return;
    }
    await chatFetch(host, 'POST', '/push/subscriptions', {
      endpoint: subscription.endpoint,
      p256dh: keys.p256dh,
      auth: keys.auth,
      user_agent: navigator.userAgent,
    });
  } catch (error) {
    console.log('Push registration failed (non-fatal):', error);
    registerStarted = false; // allow a later retry (e.g. the next mount after a transient failure)
  }
}

// ---- SW <-> app messaging -----------------------------------------------------------------

function notifyActiveConversation(conversationId) {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return;
  }
  if (navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({ type: ACTIVE_CONVERSATION_MESSAGE_TYPE, conversationId });
  }
}

// NOTE (known M2 gap, flagged not silently dropped): the pre-migration version kept the SW's
// "actively viewing" flag in sync with `dockOpen && activeConversationId` by reading the Vuex chat
// module directly. That state now lives inside the island's own store (FR-04), which host code has
// no read access to post-`mount()` (only `mount`/`unmount`/`openConversation` are exposed — see
// `@zaya-chat/ui-react`'s `mount.tsx`). Exposing it would need a NEW optional ChatHost member
// (e.g. `onActiveConversationChange?(id)`), which is a contract-change-request candidate, not
// something to add silently. Left as a no-op for M2: the practical effect is a push notification
// may still arrive for a conversation the user is already viewing, instead of being suppressed —
// a minor UX nicety regression, not a functional break (push-click-to-open still works below).
export function startActiveConversationSync() {
  notifyActiveConversation(null);
  return () => {};
}

// Handles both ways a push notification click can reach the app: an existing window gets
// postMessage'd by the SW (see public/sw.js `handleNotificationClick`); a freshly-opened window
// (no existing client) instead carries `?chat_conversation=<id>` in its URL, picked up here once.
// `onNavigate(conversationId)` is `ChatIslandMount.vue`'s own callback — see its `mounted()`, which
// calls the package-level `openConversation(el, id)` (AC-11.3, M2 addition to `@zaya-chat/ui-react`
// alongside `mount`/`unmount` — not a ChatHost seam change). Returns an unsubscribe function.
export function listenForPushNavigation(onNavigate) {
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    const conversationId = params.get(NAVIGATE_QUERY_PARAM);
    if (conversationId) {
      onNavigate(conversationId);
      params.delete(NAVIGATE_QUERY_PARAM);
      const query = params.toString();
      window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
    }
  }

  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return () => {};
  }
  const handler = (event) => {
    if (event.data && event.data.type === NAVIGATE_MESSAGE_TYPE && event.data.conversationId) {
      onNavigate(event.data.conversationId);
    }
  };
  navigator.serviceWorker.addEventListener('message', handler);
  return () => navigator.serviceWorker.removeEventListener('message', handler);
}
